/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Anchor, Divider } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import AppStack from '../../../../common/AppStack';
import AppCard from '../../../../common/AppCard';
import AppText from '../../../../common/AppText';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppImage from '../../../../common/AppImage';
import WsVariantPickerControls from './WsVariantPickerControls';
import { getSelectedEcomStoreProductVariant } from '../../../../../helpers/webstoreHelper';
import WsBundleProductVariantSelectModal from './WsBundleProductVariantSelectModal';
import { useModalState } from '../../../../../helpers/hooks';
import { Context as HelperContext } from '../../../../../providers/HelperContextProvider';
import ProductLineItemAddonsDisplay from '../../../products/ProductLineItemAddonsDisplay';
import imageNotAvailablePlaceholder from '../../../../../images/image-not-available.png';

const WsBundleVariantPicker = ({
  editModeEnabled,
  ecomStoreProduct,
  onVariantChange,
  onInputChange,
  onBundleProductVariantSelect,
  storeProductOptions,
  selectedVariantsInfo,
  selectedProductVariantsState,
  adjustments,
  submitting,
  mediaQueries,
  config,
  baseStoreUrl,
  onNavigate,
  isCartEdit,
  isBundleProduct,
  enableImageNotAvailablePreview,
  onDesignAnswerChange
}) => {
  const { toggleWebstorePreviewProductModal } = useContext(HelperContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();

  const isAccordion = false;

  const storeProductsWithReferences = ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts
    .map((p) => {
      const productOptions =
        storeProductOptions.find(
          (options) => options.pkEcomStoreProduct === p.pkEcomStoreProduct
        )?.variantOptions || [];

      const selectOptionStates = selectedProductVariantsState.filter(
        (state) => state.pkEcomStoreProduct === p.pkEcomStoreProduct
      );

      const bundleProduct = ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts.find(
        (bp) =>
          bp.fkEcomVendorProduct === p.ecomVendorProduct.pkEcomVendorProduct
      );

      const selectedProductVariants = selectOptionStates.map((s) => {
        const selectedVariantInfo = getSelectedEcomStoreProductVariant(s, [p]);

        const selectedVariant = selectedVariantInfo?.selectedVariant;
        const options =
          selectedVariant?.ecomVendorProductVariant
            .ecomVendorProductVariantOptions || [];

        const sortedOptions = [...options].sort((a, b) => a.sort - b.sort);
        const firstOption = sortedOptions[0];
        const secondOption = sortedOptions[1];

        return {
          ...selectedVariantInfo,
          sortedOptions,
          nameSort: firstOption?.value ?? '',
          secondNameSort: secondOption?.value ?? '',
          state: s,
          selectedVariant
        };
      });

      return {
        ecomStoreProduct: p,
        productOptions,
        selectOptionStates,
        bundleProduct,
        selectedProductVariants,
        previewImage: selectedProductVariants
          .map((v) => v.selectedVariant?.ecomVendorProductVariant?.previewImage)
          .filter(Boolean)[0],
        sort: bundleProduct?.sort || 0
      };
    })
    .sort((a, b) => a.sort - b.sort);

  const onViewProduct = (product) => {
    toggleWebstorePreviewProductModal({
      isOpen: true,
      ecomStoreProduct: product
    });
  };

  return (
    <AppStack>
      {isAccordion ? (
        <Accordion
          defaultValue={ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts[0]?.pkEcomStoreProduct.toString()}
          variant="separated"
        >
          <></>
        </Accordion>
      ) : (
        <AppStack style={{ gap: 24, marginTop: 8 }}>
          {storeProductsWithReferences.map((pr) => {
            const {
              ecomStoreProduct: p,
              productOptions,
              selectOptionStates,
              bundleProduct,
              selectedProductVariants,
              previewImage
            } = pr;

            const adjustmentMessage = bundleProduct
              ? adjustments[bundleProduct.fkEcomVendorProduct]
              : null;

            const hasVariantImages = p.ecomStoreProductVariants.some(
              (v) => !!v.ecomVendorProductVariant.previewImage
            );

            return (
              <React.Fragment key={p.pkEcomStoreProduct}>
                <AppStack style={{ gap: 5 }}>
                  {adjustmentMessage && (
                    <AppFlexbox
                      style={{
                        padding: 10,
                        backgroundColor: '#eca70a',
                        color: '#FFF',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <AlertCircle size={18} />{' '}
                      {adjustmentMessage.type === 'new' ? (
                        <AppText style={{ fontSize: 14 }}>
                          Product has been added
                        </AppText>
                      ) : (
                        <AppText style={{ fontSize: 14 }}>
                          The quantity has been{' '}
                          {adjustmentMessage.change > 0
                            ? 'increased'
                            : 'decreased'}{' '}
                          by{' '}
                          {adjustmentMessage.change < 0
                            ? adjustmentMessage.change * -1
                            : adjustmentMessage.change}
                        </AppText>
                      )}
                    </AppFlexbox>
                  )}

                  <AppFlexbox style={{ flex: 1, gap: 12 }}>
                    <AppCard
                      onClick={() => {
                        onViewProduct(p);
                      }}
                      radius="md"
                      style={{
                        width: 80,
                        height: 80,
                        padding: 0,
                        cursor: 'pointer'
                      }}
                      withBorder
                    >
                      <AppStack style={{ width: '100%', height: '100%' }}>
                        {!previewImage &&
                        enableImageNotAvailablePreview &&
                        hasVariantImages ? (
                          <AppImage
                            fit="contain"
                            src={imageNotAvailablePlaceholder}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          <AppImage
                            fit="contain"
                            src={
                              previewImage ||
                              p.ecomVendorProduct.previewImages[0]
                            }
                            style={{ width: '100%', height: '100%' }}
                          />
                        )}
                      </AppStack>
                    </AppCard>
                    <AppStack style={{ flex: 1, gap: 5 }}>
                      <AppFlexbox style={{ minHeight: 75 }}>
                        <AppStack
                          onClick={() => {
                            onViewProduct(p);
                          }}
                          style={{ gap: 0, cursor: 'pointer' }}
                        >
                          <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                            {p.ecomVendorProduct.name}
                          </AppText>

                          <AppFlexbox style={{ alignItems: 'center', gap: 6 }}>
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              Qty: {bundleProduct ? bundleProduct.count : 1}{' '}
                              {!submitting &&
                                bundleProduct?.count > 1 &&
                                productOptions.some(
                                  (op) => op.values.length > 1
                                ) && (
                                  <Anchor
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onOpenModal('customize_per_item', p);
                                    }}
                                    style={{
                                      fontSize: 14,
                                      color: 'dodgerblue',
                                      marginLeft: 8
                                    }}
                                  >
                                    Customize Each
                                  </Anchor>
                                )}
                            </AppText>
                          </AppFlexbox>

                          {selectedProductVariants
                            .sort(
                              (a, b) =>
                                a.nameSort.localeCompare(b.nameSort) ||
                                a.secondNameSort.localeCompare(b.secondNameSort)
                            )
                            .map((v, index) => (
                              <AppStack
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${v.selectedVariant?.ecomVendorProductVariant.pkEcomVendorProductVariant}-${index}`}
                                style={{ gap: 0 }}
                              >
                                <AppFlexbox
                                  style={{ gap: 8, alignItems: 'center' }}
                                >
                                  <AppText
                                    style={{ fontSize: 14, color: '#666' }}
                                  >
                                    {v.sortedOptions
                                      .map((o) => o.value)
                                      .join(' / ')}{' '}
                                    {selectedProductVariants.length > 1 &&
                                      `(x${v.state.count})`}
                                  </AppText>

                                  {(v.outOfStock || v.tooManyInCart) && (
                                    <AppFlexbox
                                      style={{
                                        alignItems: 'center',
                                        gap: 5
                                      }}
                                    >
                                      <AlertCircle color="#C40000" size={15} />
                                      <AppText
                                        style={{
                                          fontSize: 12,
                                          color: '#C40000'
                                        }}
                                      >
                                        {v.outOfStock
                                          ? 'Out of stock'
                                          : 'Quantity not available'}
                                      </AppText>
                                    </AppFlexbox>
                                  )}
                                </AppFlexbox>
                                {v.state.ecomStoreCartProductInputAnswers
                                  .length > 0 && (
                                  <AppStack style={{ gap: 0, paddingLeft: 12 }}>
                                    <ProductLineItemAddonsDisplay
                                      addOns={v.state.ecomStoreCartProductInputAnswers.map(
                                        (i) => {
                                          const input = v.product.ecomVendorProduct.ecomVendorProductInputs.find(
                                            (f) =>
                                              f.pkEcomVendorProductInput ===
                                              i.fkEcomVendorProductInput
                                          );

                                          return {
                                            sort: i.sort,
                                            value: i.previewImage || i.value,
                                            name: input?.label,
                                            unitAmountInCents:
                                              input?.adjustedCost || 0,
                                            fkRegFormControlType:
                                              input?.fkRegFormControlType
                                          };
                                        }
                                      )}
                                    />
                                  </AppStack>
                                )}
                              </AppStack>
                            ))}
                        </AppStack>
                      </AppFlexbox>

                      {selectOptionStates.length <= 1 ? (
                        productOptions.some((op) => op.values.length > 1) && (
                          <WsVariantPickerControls
                            baseStoreUrl={baseStoreUrl}
                            config={config}
                            ecomStoreProduct={p}
                            isBundleProduct={isBundleProduct}
                            isCartEdit={isCartEdit}
                            mediaQueries={mediaQueries}
                            onDesignAnswerChange={onDesignAnswerChange}
                            onInputChange={onInputChange}
                            onNavigate={onNavigate}
                            onVariantChange={onVariantChange}
                            selectedProductVariantsState={selectOptionStates[0]}
                            selectedVariant={
                              selectedProductVariants[0]?.selectedVariant
                            }
                            submitting={submitting}
                            variantOptions={productOptions}
                          />
                        )
                      ) : (
                        <></>
                      )}
                    </AppStack>
                  </AppFlexbox>
                </AppStack>
                <Divider />
              </React.Fragment>
            );
          })}
        </AppStack>
      )}

      <WsBundleProductVariantSelectModal
        baseStoreUrl={baseStoreUrl}
        config={config}
        ecomStoreProduct={ecomStoreProduct}
        editModeEnabled={editModeEnabled}
        isBundleProduct={isBundleProduct}
        isCartEdit={isCartEdit}
        isOpen={modalState.isOpen && modalState.action === 'customize_per_item'}
        mediaQueries={mediaQueries}
        onClose={onCloseModal}
        onConfirm={(optionStates) => {
          onBundleProductVariantSelect(
            modalState.item.pkEcomStoreProduct,
            optionStates
          );
          onCloseModal();
        }}
        onNavigate={onNavigate}
        packageEcomStoreProduct={modalState.item}
        selectedProductVariantsState={selectedProductVariantsState}
        storeProductOptions={storeProductOptions}
      />
    </AppStack>
  );
};

WsBundleVariantPicker.propTypes = {
  adjustments: PropTypes.object,
  baseStoreUrl: PropTypes.string,
  config: PropTypes.object,
  ecomStoreProduct: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  enableImageNotAvailablePreview: PropTypes.bool,
  isBundleProduct: PropTypes.bool,
  isCartEdit: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onBundleProductVariantSelect: PropTypes.func,
  onDesignAnswerChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onNavigate: PropTypes.func,
  onVariantChange: PropTypes.func,
  selectedProductVariantsState: PropTypes.array,
  selectedVariantsInfo: PropTypes.array,
  storeProductOptions: PropTypes.array,
  submitting: PropTypes.bool
};

export default WsBundleVariantPicker;
